import React from "react";
import { MONTHS } from "../config";
import { Link } from "react-router-dom";
import { FiFileText } from "react-icons/fi";

function Terms() {
    const now = new Date();
    const last_update = MONTHS[now.getMonth()] + ' ' + now.getFullYear();
    return (
        <div className="min-h-screen pt-20 pb-24">
            {/* Hero Banner */}
            <div className="relative bg-gradient-to-br from-[#6366f1] via-[#8b5cf6] via-[#a855f7] to-[#ec4899] overflow-hidden rounded-b-3xl border-b-4 border-[#fbbf24]/60 shadow-2xl shadow-[#8b5cf6]/50">
                {/* Cosmic nebula overlay */}
                <div className="absolute inset-0 bg-gradient-to-tr from-[#3b82f6]/20 via-transparent to-[#f472b6]/20"></div>
                <div className="absolute inset-0 bg-gradient-to-bl from-[#fbbf24]/10 via-transparent to-[#06b6d4]/10"></div>
                
                {/* Space decorations */}
                <div className="absolute top-4 left-4 text-4xl animate-bounce-joy">📜</div>
                <div className="absolute top-8 right-8 text-3xl animate-float">📋</div>
                <div className="absolute bottom-4 left-8 text-3xl animate-wiggle">⚖️</div>
                <div className="absolute bottom-8 right-4 text-4xl animate-bounce-joy" style={{ animationDelay: '1s' }}>✨</div>
                <div className="absolute top-1/2 left-1/4 text-2xl animate-twinkle">💫</div>
                <div className="absolute top-1/3 right-1/4 text-2xl animate-float" style={{ animationDelay: '0.5s' }}>📝</div>
                
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 relative z-10 text-center">
                    <div className="inline-block p-4 bg-white/20 rounded-full mb-6 backdrop-blur-sm">
                        <FiFileText className="text-white text-5xl drop-shadow-lg" />
                    </div>
                    <h1 className="text-4xl md:text-5xl font-extrabold text-white mb-4 drop-shadow-lg animate-bounce-joy bg-gradient-to-r from-[#fef3c7] via-white to-[#fef3c7] bg-clip-text text-transparent">
                        Terms of Service
                    </h1>
                    <div className="w-20 h-1 bg-gradient-to-r from-[#fbbf24] via-[#ec4899] to-[#3b82f6] mb-4 mx-auto rounded-full shadow-lg shadow-[#8b5cf6]/60"></div>
                    <p className="text-white/95 text-lg">Last updated: {last_update}</p>
                </div>
            </div>

            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
                {/* Introduction */}
                <section className="mb-12">
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl">
                        <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 drop-shadow-lg">General Terms and Conditions of Use</h2>
                        <p className="text-white/90 text-lg leading-relaxed">
                            Using any application developed by JoyLoomGames constitutes full acceptance of these Terms and Conditions.
                            Our goal is to provide a safe, healthy, and responsible environment for all users. Please read the following
                            terms carefully before using the applications.
                        </p>
                    </div>
                </section>

                {/* Section 1 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#6366f1]/30 via-[#8b5cf6]/20 to-[#ec4899]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#fbbf24]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#6366f1] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                            Membership and Usage Conditions
                        </h3>
                        <div className="space-y-4 text-white/90">
                            <p className="leading-relaxed">
                                Membership is permitted for users aged <span className="font-semibold text-white">13 and above</span>.
                                Users under 13 may register only with the consent of a legal guardian.
                            </p>
                            <p className="leading-relaxed">
                                For applications requiring registration, users must provide <span className="font-semibold text-white">accurate, up-to-date, and real information</span>.
                            </p>
                            <p className="leading-relaxed">
                                Creating fake, duplicate, or impersonated accounts is <span className="font-semibold text-red-300">strictly prohibited</span>.
                            </p>
                            <p className="leading-relaxed">
                                Users are responsible for protecting their account information (passwords, etc.).
                            </p>
                        </div>
                    </div>
                </section>

                {/* Section 2 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#3b82f6]/30 via-[#6366f1]/20 to-[#8b5cf6]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#3b82f6]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#3b82f6] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                            User Responsibilities and Conduct
                        </h3>
                        <p className="mb-4 text-white/90 leading-relaxed">
                            Users must refrain from posting, sharing, or sending any content that includes:
                        </p>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Immoral, obscene, violent, or offensive material</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Insults, slander, threats, or mockery of others</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Unauthorized advertisements or spam</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Rumors, false news, or misleading information</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Racist, ethnic, or provocative content</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Content violating national laws or others' intellectual property rights</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Anti-religious or anti-government material</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Threats, intimidation, or impersonation of others</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Altering, simulating, or hacking the product</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Storing or damaging in-game content such as profiles or images</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Copying, modifying, translating, or selling any game content developed by the developer</span>
                            </li>
                        </ul>
                        <p className="mt-6 text-white/90 leading-relaxed font-semibold">
                            Any misuse of the game's features to deceive, threaten, or harass others is strictly prohibited.
                        </p>
                    </div>
                </section>

                {/* Section 3 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#ec4899]/30 via-[#f472b6]/20 to-[#fb7185]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#ec4899]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#ec4899] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                            Rules for Social and Hidden-Role Games
                        </h3>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Inappropriate usernames may result in <span className="font-semibold text-white">permanent account suspension</span>.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Any insult or disrespect toward other players is considered misconduct.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Disrespect toward any ethnicity or religion results in a <span className="font-semibold text-red-300">permanent ban</span>.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>These games are intended for users aged <span className="font-semibold text-white">18 and above</span>; underage use is not permitted.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Political discussions are strictly forbidden and will result in penalties.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>All game images and sounds are the property of JoyLoom Studio; unauthorized use is subject to legal action.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Swearing or making others swear is a violation.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Declaring or assigning roles to oneself or others is prohibited and punishable.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Each person may only have one account.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Phone numbers are used solely for identification, login, and preventing duplicate accounts.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Collusion or cheating in the game will lead to account suspension.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 4 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#fbbf24]/30 via-[#f59e0b]/20 to-[#d97706]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#fbbf24]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#fbbf24] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                            User Communication and Interaction
                        </h3>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Messaging must always be respectful and comply with national laws.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Sending inappropriate messages, requesting personal information, or attempting to influence others is prohibited.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>In case of violations, users should report them using the in-app reporting tools.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 5 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#10b981]/30 via-[#059669]/20 to-[#047857]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#10b981]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#10b981] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">5</span>
                            Developer Responsibilities
                        </h3>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>The developer strives to provide a safe environment for all users.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Users are solely responsible for the content they create or share.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>The developer reserves the right to restrict access or suspend accounts that violate these rules.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Continued use of the application after updates to these terms implies acceptance of the new version.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 6 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-[#a855f7]/30 via-[#9333ea]/20 to-[#7c3aed]/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-[#a855f7]/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-[#a855f7] rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">6</span>
                            Privacy Policy
                        </h3>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Users' personal information is treated as <span className="font-semibold text-white">confidential and will be protected</span>.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Login details and phone numbers will not be shared without consent, except as required by law.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>The developer bears no responsibility for user negligence in protecting account data.</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-white mt-1">•</span>
                                <span>Games may contain advertisements; their type and placement may change without prior notice.</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Section 7 */}
                <section className="mb-8">
                    <div className="bg-gradient-to-br from-red-600/30 via-red-700/20 to-red-800/30 backdrop-blur-sm rounded-2xl p-8 border-2 border-red-500/40 shadow-xl">
                        <h3 className="text-2xl font-bold mb-4 text-white flex items-center gap-2">
                            <span className="bg-white text-red-600 rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">7</span>
                            Violations and Consequences
                        </h3>
                        <p className="mb-4 text-white/90 leading-relaxed">
                            Depending on the severity of the violation, the following actions may be taken:
                        </p>
                        <ul className="space-y-3 text-white/90">
                            <li className="flex items-start gap-3">
                                <span className="text-red-300 mt-1">⚠</span>
                                <span className="font-semibold">Content removal</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-red-300 mt-1">⚠</span>
                                <span className="font-semibold">Restricted access to certain features</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-red-300 mt-1">⚠</span>
                                <span className="font-semibold">Temporary or permanent account suspension</span>
                            </li>
                            <li className="flex items-start gap-3">
                                <span className="text-red-300 mt-1">⚠</span>
                                <span className="font-semibold">Legal action (if necessary)</span>
                            </li>
                        </ul>
                    </div>
                </section>

                {/* Footer Note */}
                <section className="mt-12">
                    <div className="bg-gradient-to-br from-white/10 via-white/5 to-transparent backdrop-blur-sm rounded-2xl p-8 border border-white/20 shadow-xl text-center">
                        <p className="text-white/90 text-sm mb-2">
                            By using our applications, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
                        </p>
                        <p className="text-white/90 text-sm">
                            For questions or concerns, please <Link to="/contact" className="text-white hover:text-white/80 underline font-semibold">contact us</Link>.
                        </p>
                    </div>
                </section>
            </main>
        </div>
    );
}

export default Terms;
